import {Action, ButtonActionProps, DropdownActionMenuItem} from 'platform/components';
import {themeIcons, ThemeIconKey} from 'platform/foundation';

import {MouseEventHandler} from 'react';

export interface ResponsiveWidgetActions {
  primaryAction: Action;
  overflowAction?: Action;
}

// `ButtonActionProps['leftIcon']` allows `ThemeIconKey | ThemeLottieKey`, but
// `DropdownActionMenuItem['leftIcon']` only accepts `ThemeIconKey`. Lottie
// animations are not supported in dropdown items, so we narrow the value here.
function isThemeIconKey(value: unknown): value is ThemeIconKey {
  return typeof value === 'string' && value in themeIcons;
}

/**
 * Splits a flat list of `Action`s into a mobile-friendly shape:
 * the first action stays visible (full-width when it is a standalone
 * button), and the remaining button-typed actions collapse into a
 * single overflow dropdown.
 *
 * Returns `null` when there are no actions at all.
 */
export function getResponsiveWidgetActions(actions: Action[]): ResponsiveWidgetActions | null {
  const [firstAction, ...restActions] = actions;

  if (!firstAction) {
    return null;
  }

  const menuItems: DropdownActionMenuItem[] = restActions
    .filter(
      (action): action is ButtonActionProps & {onClick: MouseEventHandler<HTMLButtonElement>} =>
        action.type === 'button' && typeof action.onClick === 'function'
    )
    .map((action) => ({
      label: action.title,
      onClick: action.onClick,
      leftIcon: isThemeIconKey(action.leftIcon) ? action.leftIcon : undefined,
    }));

  // Stretch only standalone button-like actions; dropdown-iconButton, iconButton,
  // chips, custom etc. should keep their natural width on mobile.
  const primaryAction: Action =
    firstAction.type === 'button' || firstAction.type === 'buttonWithTooltip'
      ? {...firstAction, isFullWidth: true}
      : firstAction;

  return {
    primaryAction,
    overflowAction:
      menuItems.length > 0
        ? {
            type: 'dropdown-iconButton',
            priority: 'secondary',
            menuItems,
          }
        : undefined,
  };
}
